import { useState } from 'react';
import { Box, Button, Grid, TextField, Typography, CircularProgress } from '@mui/material';
import { z } from 'zod';
import axiosInstance from '../services/axiosInstance';
import SuccessPopup from './SuccessPopup';

// --- 1. Define Zod Schema ---
const enquirySchema = z.object({
  name: z.string().min(1, "Name is required"),
  mobile: z.string()
    .min(8, "Mobile number must be at least 8 digits")
    .regex(/^[0-9]+$/, "Mobile number must contain only digits"),
});

type EnquiryFormData = z.infer<typeof enquirySchema>;

const EnquirySection = () => {
  // --- State ---
  const [formData, setFormData] = useState<EnquiryFormData>({ name: '', mobile: '' });
  const [errors, setErrors] = useState<Partial<EnquiryFormData>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  // --- Handlers ---
  const handleChange = (field: keyof EnquiryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEnquiry = async () => {
    const validationResult = enquirySchema.safeParse(formData);

    if (!validationResult.success) {
      const fieldErrors: any = {};
      validationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0]] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post(
        "/CustomerVehicle/CustomerEnquiryCreate",
        {
          customerName: formData.name,
          mobileNo: formData.mobile
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data as {
        responseCode: string;
        responseMessage?: string;
      };

      if (result.responseCode === "00") {
        setShowSuccess(true);
        setFormData({ name: '', mobile: '' });
      } else {
        console.error("API Error:", result.responseMessage);
      }

    } catch (error) {
      console.error("❌ Error submitting enquiry", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#f3f4f6",
          py: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 },
          textAlign: "center",
        }}
      >
        <Grid
          container
          spacing={4}
          justifyContent="center"
          alignItems="center"
          sx={{ maxWidth: 900, mx: "auto" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ mb: 1, color: "#0f172a" }}
            >
              Have a Question About Our Services?
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, color: "#4b5563" }}>
              Our experts are ready to provide a tailored consultation for your commercial goals.
            </Typography>

            <Box sx={{ maxWidth: 340, mx: "auto" }}>
              
              {/* --- Name Input --- */}
              <TextField
                fullWidth
                placeholder="Name"
                size="small"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={loading}
                error={!!errors.name}
                helperText={errors.name}
                sx={{
                  mb: 1.5,
                  // We target the input root specifically for the background
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: 1,
                  }
                }}
              />

              {/* --- Mobile Input --- */}
              <TextField
                fullWidth
                placeholder="Mobile No."
                size="small"
                value={formData.mobile}
                onChange={(e) => handleChange('mobile', e.target.value)}
                disabled={loading}
                error={!!errors.mobile}
                helperText={errors.mobile}
                sx={{
                  mb: 2,
                  // We target the input root specifically for the background
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: 1,
                  }
                }}
              />

              <Button
                fullWidth
                onClick={handleEnquiry}
                disabled={loading}
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  border: '1px solid #ccc',
                  backgroundColor: '#FFB733',
                  color: '#000',
                  py: 1.2,
                  transition: 'all 0.3s ease',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#e7c382ff',
                    color: '#fff',
                    boxShadow: '0px 6px 12px rgba(255, 177, 51, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#000' }} />
                ) : (
                  "Enquire Now"
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SuccessPopup 
        open={showSuccess} 
        onClose={() => setShowSuccess(false)} 
      />
    </>
  );
};

export default EnquirySection;